import { yearBoundsFromParam } from "@/lib/faceted-search-url";
import { formatPrice } from "@/lib/format-price";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { ALBUM_TYPES, type AlbumType } from "@garimpa/lib/albumTypes";
import { OFFER_YEAR_MAX, OFFER_YEAR_MIN } from "@garimpa/lib/buildOffersPageLink";
import { isCatalogId } from "@garimpa/lib/catalogId";
import { formatCountry } from "@garimpa/lib/countries";
import { DISCOVERY_DECADES } from "@garimpa/lib/discoveryDecades";
import {
  FORMAT_CATEGORIES,
  FORMAT_CATEGORIES_WITH_OTHER,
  type FormatCategoryWithOther,
} from "@garimpa/lib/formatGroups";
import type { DiscographyTypeCounts } from "@garimpa/lib/resolveDiscographyDefaultType";
import { getSearchSerialization } from "@garimpa/lib/searchSerialization";
import { taxonomyCatalogByLocale } from "@garimpa/lib/taxonomyCatalog";
import { valueToArray } from "@garimpa/lib/valueToArray";
import { DEFAULT_VIEWER_CURRENCY } from "@garimpa/lib/viewerPreferences";

type LooseTranslator = ((key: never, values?: any) => string) & {
  has?: (key: never) => boolean;
};

type SearchType = (typeof ALBUM_TYPES)[number];
type SearchFormat = (typeof FORMAT_CATEGORIES)[number];
type OffersFormat = FormatCategoryWithOther;
type OffersSort = "best_price" | "price_drop" | "recent";

const { stringifySearch } = getSearchSerialization();

export interface SearchSeoInput {
  q?: string;
  tab?: string;
  genre?: string;
  style?: string;
  type?: string;
  format?: string;
  country?: string;
  origin?: string;
  year?: number[];
  label?: string[];
  company?: string[];
  page?: number;
  forSale?: boolean;
  sort?: string;
  cursor?: string;
}

export interface SearchSeoTranslators {
  seo: LooseTranslator;
  search: LooseTranslator;
  albumType: LooseTranslator;
  formats: LooseTranslator;
  genres: LooseTranslator;
  styles: LooseTranslator;
}

export interface OffersSeoInput {
  condition?: "new" | "used";
  artistId?: string | string[];
  artistName?: string;
  currency?: string;
  cursor?: number;
  format?: string;
  genre?: string | string[];
  priceMin?: number;
  priceMax?: number;
  sort?: string;
  style?: string | string[];
  year?: number[];
}

export interface OffersSeoTranslators {
  offers: LooseTranslator;
  collection: LooseTranslator;
  genres: LooseTranslator;
  styles: LooseTranslator;
}

export interface EntitySeoTranslators {
  seo: LooseTranslator;
  genres: LooseTranslator;
  styles: LooseTranslator;
}

const TITLE_LIMIT = 70;
const DESCRIPTION_LIMIT = 160;
const TITLE_TYPE_ORDER: AlbumType[] = ["album", "compilation", "single_ep", "live", "video"];
const HTML_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
};

function translate(
  t: LooseTranslator,
  key: string,
  fallback: string,
  values?: Record<string, unknown>,
): string {
  const typedKey = key as never;
  if (typeof t.has === "function" && !t.has(typedKey)) return fallback;
  try {
    return t(typedKey, values);
  } catch {
    return fallback;
  }
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function decodeHtmlEntities(value: string): string {
  return value.replace(/&(#\d+|#x[\da-f]+|[a-z][\da-z]+);/gi, (entity, token: string) => {
    if (token.startsWith("#x")) {
      const codepoint = Number.parseInt(token.slice(2), 16);
      return Number.isFinite(codepoint) ? String.fromCodePoint(codepoint) : entity;
    }
    if (token.startsWith("#")) {
      const codepoint = Number.parseInt(token.slice(1), 10);
      return Number.isFinite(codepoint) ? String.fromCodePoint(codepoint) : entity;
    }
    return HTML_ENTITIES[token.toLowerCase()] ?? entity;
  });
}

function sentenceCase(value: string, locale: string): string {
  const cleaned = normalizeWhitespace(value);
  const [first = "", ...rest] = Array.from(cleaned);
  return `${first.toLocaleUpperCase(locale)}${rest.join("")}`;
}

export function cleanSeoText(value: string | null | undefined): string | null {
  if (!value) return null;
  const cleaned = normalizeWhitespace(
    decodeHtmlEntities(value)
      .replace(/<[^>]*>/g, "")
      .replace(/\{\{[^}]+\}\}/g, ""),
  );
  return cleaned || null;
}

function inlineSeoText(value: string | null | undefined): string {
  return cleanSeoText(value) ?? "";
}

export function clipSeoText(value: string, maxLength = DESCRIPTION_LIMIT): string {
  const cleaned = normalizeWhitespace(decodeHtmlEntities(value));
  if (cleaned.length <= maxLength) return cleaned;
  const sliced = cleaned.slice(0, maxLength - 1);
  const lastSpace = sliced.lastIndexOf(" ");
  const clipped = lastSpace > maxLength * 0.65 ? sliced.slice(0, lastSpace) : sliced;
  return `${clipped.trimEnd()}…`;
}

export function trimSeoTitle(title: string, maxLength = TITLE_LIMIT): string {
  return clipSeoText(title, maxLength);
}

export function buildSeoDescription(parts: Array<string | null | undefined>): string {
  const cleanedParts = parts
    .map((part) => cleanSeoText(part))
    .filter((part): part is string => Boolean(part));
  const description = cleanedParts.reduce((text, part) => {
    if (!text) return part;
    const separator = /[.!?…]$/u.test(text) ? " " : ". ";
    return `${text}${separator}${part}`;
  }, "");

  return clipSeoText(description);
}

export function formatSeoTaxonomyList(input: {
  genres?: Array<string | null | undefined> | null;
  styles?: Array<string | null | undefined> | null;
  translators: Pick<EntitySeoTranslators, "genres" | "styles">;
  locale: string;
  limit?: number;
}): string | null {
  const labels = uniqueNonEmpty([
    ...(input.genres ?? []).map((genre) =>
      genre ? formatTranslatedTaxonomy(genre, input.translators.genres) : null,
    ),
    ...(input.styles ?? []).map((style) =>
      style ? formatTranslatedTaxonomy(style, input.translators.styles) : null,
    ),
  ]).slice(0, input.limit ?? 4);

  return labels.length > 0 ? listFormat(input.locale, labels) : null;
}

export function formatSeoCountryList(
  countryCodes: Array<string | null | undefined> | string | null | undefined,
  locale: string,
): string | null {
  const codes = valueToArray(countryCodes);
  const countries = uniqueNonEmpty(
    codes.map((countryCode) =>
      countryCode ? (formatCountry(countryCode, locale) ?? countryCode) : null,
    ),
  );

  return countries.length > 0 ? listFormat(locale, countries) : null;
}

export function formatSeoNameList(
  names: Array<string | null | undefined> | null | undefined,
  locale: string,
  limit = 5,
): string | null {
  const cleaned = uniqueNonEmpty(names ?? []).slice(0, limit);
  return cleaned.length > 0 ? listFormat(locale, cleaned) : null;
}

export function buildArtistSeoDescription(input: {
  name: string;
  typeLabels: string | null;
  formats: string;
  country: string | null;
  members: string | null;
  tags: string | null;
  tSeo: LooseTranslator;
  locale: string;
}): string {
  const name = inlineSeoText(input.name);
  const typeLabels = cleanSeoText(input.typeLabels);
  const details = buildFactList(
    [
      input.country
        ? translate(input.tSeo, "artistCountryFact", `from ${input.country}`, {
            country: input.country,
          })
        : null,
      input.members
        ? translate(input.tSeo, "artistMembersFact", `members ${input.members}`, {
            members: input.members,
          })
        : null,
      input.tags
        ? translate(input.tSeo, "artistTagsFact", `genres and styles ${input.tags}`, {
            tags: input.tags,
          })
        : null,
    ],
    input.locale,
  );
  const key = typeLabels
    ? details
      ? "artistDescriptionWithFacts"
      : "artistDescription"
    : details
      ? "artistDescriptionFallbackWithFacts"
      : "artistDescriptionFallback";
  const fallback = typeLabels
    ? details
      ? `Browse ${name}'s ${typeLabels} discography on ${input.formats}. Open an album to compare current offers, prices, and availability. ${details}`
      : `Browse ${name}'s ${typeLabels} discography on ${input.formats}. Open an album to compare current offers, prices, and availability`
    : details
      ? `Browse ${name}'s discography on ${input.formats}. Open an album to compare current offers, prices, and availability. ${details}`
      : `Browse ${name}'s discography on ${input.formats}. Open an album to compare current offers, prices, and availability`;

  return buildSeoDescription([
    translate(input.tSeo, key, fallback, {
      name,
      types: typeLabels,
      formats: input.formats,
      details,
    }),
  ]);
}

export function buildCompanySeoDescription(input: {
  name: string;
  typeLabels: string | null;
  formats: string;
  countries: string | null;
  parentName: string | null;
  distinctReleaseCount?: number | null;
  distinctWorkCount?: number | null;
  distinctArtistCount?: number | null;
  tSeo: LooseTranslator;
  locale: string;
}): string {
  const name = inlineSeoText(input.name);
  const typeLabels = cleanSeoText(input.typeLabels);
  const details = buildCompanyDescriptionFacts(input);
  const { key, fallback } = selectDescriptionVariant(Boolean(typeLabels), Boolean(details), {
    primary: {
      key: "companyDescription",
      fallback: `Browse the ${name} ${typeLabels} catalog on ${input.formats}. Open an album to compare current offers, prices, and availability`,
    },
    primaryWithFacts: {
      key: "companyDescriptionWithFacts",
      fallback: `Browse the ${name} ${typeLabels} catalog on ${input.formats}. Open an album to compare current offers, prices, and availability. ${details}`,
    },
    fallback: {
      key: "companyDescriptionFallback",
      fallback: `Browse the ${name} catalog on ${input.formats}. Open an album to compare current offers, prices, and availability`,
    },
    fallbackWithFacts: {
      key: "companyDescriptionFallbackWithFacts",
      fallback: `Browse the ${name} catalog on ${input.formats}. Open an album to compare current offers, prices, and availability. ${details}`,
    },
  });

  return buildSeoDescription([
    translate(input.tSeo, key, fallback, {
      name,
      types: typeLabels,
      formats: input.formats,
      details,
    }),
  ]);
}

function buildCompanyDescriptionFacts(
  input: Parameters<typeof buildCompanySeoDescription>[0],
): string | null {
  return buildFactList(
    [
      input.countries
        ? translate(input.tSeo, "companyCountryFact", `countries ${input.countries}`, {
            countries: input.countries,
          })
        : null,
      input.parentName
        ? translate(input.tSeo, "companyParentFact", `part of ${input.parentName}`, {
            parent: input.parentName,
          })
        : null,
      positiveCount(input.distinctReleaseCount)
        ? translate(
            input.tSeo,
            "companyReleaseCountFact",
            `${input.distinctReleaseCount} releases`,
            { count: input.distinctReleaseCount },
          )
        : null,
      positiveCount(input.distinctWorkCount)
        ? translate(input.tSeo, "companyWorkCountFact", `${input.distinctWorkCount} works`, {
            count: input.distinctWorkCount,
          })
        : null,
      positiveCount(input.distinctArtistCount)
        ? translate(input.tSeo, "companyArtistCountFact", `${input.distinctArtistCount} artists`, {
            count: input.distinctArtistCount,
          })
        : null,
    ],
    input.locale,
  );
}

type SeoDescriptionVariant = { key: string; fallback: string };

function selectDescriptionVariant(
  hasPrimary: boolean,
  hasFacts: boolean,
  variants: {
    primary: SeoDescriptionVariant;
    primaryWithFacts: SeoDescriptionVariant;
    fallback: SeoDescriptionVariant;
    fallbackWithFacts: SeoDescriptionVariant;
  },
): SeoDescriptionVariant {
  if (hasPrimary) return hasFacts ? variants.primaryWithFacts : variants.primary;
  return hasFacts ? variants.fallbackWithFacts : variants.fallback;
}

export function buildWorkSeoDescription(input: {
  title: string;
  artist: string;
  formats: string;
  year: number | null | undefined;
  releaseCount?: number | null;
  tags: string | null;
  tSeo: LooseTranslator;
  locale: string;
}): string {
  const title = inlineSeoText(input.title);
  const artist = inlineSeoText(input.artist);
  const releaseCount = positiveCount(input.releaseCount) ? input.releaseCount : null;
  const tags = cleanSeoText(input.tags);
  const details = buildWorkDescriptionFacts(releaseCount, tags, input.tSeo);
  const { key, fallback } = selectDescriptionVariant(Boolean(input.year), Boolean(details), {
    primary: {
      key: "workDescriptionReleased",
      fallback: `${title} by ${artist} (${input.year}) on ${input.formats}. Compare physical editions, current offers, prices, availability, and price history`,
    },
    primaryWithFacts: {
      key: "workDescriptionReleasedWithFacts",
      fallback: `${title} by ${artist} (${input.year}) on ${input.formats}. Compare current offers, prices, availability, and price history. ${details}`,
    },
    fallback: {
      key: "workDescription",
      fallback: `${title} by ${artist} on ${input.formats}. Compare physical editions, current offers, prices, availability, and price history`,
    },
    fallbackWithFacts: {
      key: "workDescriptionWithFacts",
      fallback: `${title} by ${artist} on ${input.formats}. Compare current offers, prices, availability, and price history. ${details}`,
    },
  });

  return buildSeoDescription([
    translate(input.tSeo, key, fallback, {
      title,
      artist,
      formats: input.formats,
      year: input.year,
      details,
    }),
  ]);
}

function buildWorkDescriptionFacts(
  releaseCount: number | null,
  tags: string | null,
  tSeo: LooseTranslator,
): string | null {
  if (releaseCount && tags) {
    return translate(
      tSeo,
      "workReleaseCountAndTagsFact",
      `${releaseCount} known releases across ${tags}`,
      { count: releaseCount, tags },
    );
  }
  if (releaseCount) {
    return translate(tSeo, "workReleaseCountFact", `${releaseCount} known releases`, {
      count: releaseCount,
    });
  }
  return tags ? translate(tSeo, "workTagsFact", `styles ${tags}`, { tags }) : null;
}

export function buildReleaseSeoDescription(input: {
  title: string;
  artist: string;
  formatSummary: string | null;
  hasOffers?: boolean;
  releasedAt: string | null;
  country: string | null;
  label: string | null;
  tags: string | null;
  tSeo: LooseTranslator;
  locale: string;
}): string {
  const title = inlineSeoText(input.title);
  const artist = inlineSeoText(input.artist);
  const formatSummary =
    formatSeoTitleFormat(input.formatSummary) ??
    formatSeoTitleFormat(translate(input.tSeo, "releaseFormatFallback", "physical release")) ??
    "Physical release";
  const releasedAt = cleanSeoText(input.releasedAt);
  const details = buildReleaseDescriptionFacts(input);
  const base = `${title} by ${artist} on ${formatSummary}`;
  const keyPrefix = input.hasOffers ? "releaseCommerceDescription" : "releaseDescription";
  const commerceSuffix = input.hasOffers
    ? ". Compare current offers, prices, availability, and price history"
    : "";
  const { key, fallback } = selectDescriptionVariant(Boolean(releasedAt), Boolean(details), {
    primary: { key: `${keyPrefix}Released`, fallback: `${base}, ${releasedAt}${commerceSuffix}` },
    primaryWithFacts: {
      key: `${keyPrefix}ReleasedWithFacts`,
      fallback: `${base}, ${releasedAt}${commerceSuffix}. ${details}`,
    },
    fallback: { key: keyPrefix, fallback: `${base}${commerceSuffix}` },
    fallbackWithFacts: {
      key: `${keyPrefix}WithFacts`,
      fallback: `${base}${commerceSuffix}. ${details}`,
    },
  });

  return buildSeoDescription([
    translate(input.tSeo, key, fallback, {
      title,
      artist,
      format: formatSummary,
      releasedAt,
      details,
    }),
  ]);
}

function buildReleaseDescriptionFacts(
  input: Parameters<typeof buildReleaseSeoDescription>[0],
): string | null {
  return buildFactList(
    [
      input.country
        ? translate(input.tSeo, "releaseCountryFact", `${input.country} edition`, {
            country: input.country,
          })
        : null,
      input.label
        ? translate(input.tSeo, "releaseLabelFact", `label ${input.label}`, {
            label: input.label,
          })
        : null,
      input.tags
        ? translate(input.tSeo, "releaseTagsFact", `genres and styles ${input.tags}`, {
            tags: input.tags,
          })
        : null,
    ],
    input.locale,
  );
}

function listFormat(locale: string, values: string[], options?: Intl.ListFormatOptions): string {
  if (values.length === 0) return "";
  return new Intl.ListFormat(locale, {
    type: "conjunction",
    style: "long",
    ...options,
  }).format(values);
}

function buildFactList(values: Array<string | null | undefined>, _locale: string): string | null {
  const facts = uniqueNonEmpty(values);
  return facts.length > 0 ? facts.join(", ") : null;
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const cleaned = cleanSeoText(value);
    if (!cleaned || seen.has(cleaned)) continue;
    seen.add(cleaned);
    result.push(cleaned);
  }
  return result;
}

function positiveCount(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function formatAvailableTypeLabels(
  typeCounts: DiscographyTypeCounts | null | undefined,
  tSeo: LooseTranslator,
  locale: string,
): string | null {
  const labels = TITLE_TYPE_ORDER.filter((type) => (typeCounts?.[type] ?? 0) > 0).map((type) =>
    inlineSeoText(translate(tSeo, `typeLabels.${type}`, fallbackTypeLabel(type))),
  );
  return labels.length > 0 ? listFormat(locale, labels) : null;
}

export function buildReleaseSeoFormat(input: {
  formatCategories: Array<string | null | undefined>;
  tSeo: LooseTranslator;
  locale: string;
}): string | null {
  const categories = new Set(input.formatCategories.filter(isSearchFormat));
  const labels = FORMAT_CATEGORIES.filter((category) => categories.has(category)).map((category) =>
    inlineSeoText(
      translate(
        input.tSeo,
        `releaseFormatLabels.${category}`,
        translate(input.tSeo, `formatLabels.${category}`, fallbackFormatLabel(category)),
      ),
    ),
  );
  return labels.length > 0 ? sentenceCase(listFormat(input.locale, labels), input.locale) : null;
}

export function buildDefaultCommerceSeoFormat(input: {
  tSeo: LooseTranslator;
  locale: string;
}): string {
  return (
    buildReleaseSeoFormat({
      formatCategories: ["vinyl", "cd"],
      tSeo: input.tSeo,
      locale: input.locale,
    }) ?? "Vinyl and CD"
  );
}

export function extractReleaseSeoYear(date: string | null | undefined): string | null {
  const match = date?.match(/^(\d{4})/);
  return match?.[1] ?? null;
}

export function buildArtistSeoTitle(input: {
  name: string;
  formats: string;
  tSeo: LooseTranslator;
}): string {
  const name = inlineSeoText(input.name);
  const title = translate(input.tSeo, "artistTitle", `${name} on ${input.formats}`, {
    name,
    formats: input.formats,
  });
  return trimSeoTitle(title);
}

export function buildCompanySeoTitle(input: {
  name: string;
  formats: string;
  tSeo: LooseTranslator;
}): string {
  const name = inlineSeoText(input.name);
  const title = translate(input.tSeo, "companyTitle", `${name} ${input.formats} catalog`, {
    name,
    formats: input.formats,
  });
  return trimSeoTitle(title);
}

export function buildWorkSeoTitle(input: {
  title: string;
  artist: string;
  formats: string;
  year: number | null | undefined;
  tSeo: LooseTranslator;
}): string {
  const title = inlineSeoText(input.title);
  const artist = inlineSeoText(input.artist);
  const key = input.year ? "workTitleReleased" : "workTitle";
  const fallback = input.year
    ? `${title} by ${artist} on ${input.formats} (${input.year})`
    : `${title} by ${artist} on ${input.formats}`;

  return trimSeoTitle(
    translate(input.tSeo, key, fallback, {
      title,
      artist,
      formats: input.formats,
      year: input.year,
    }),
  );
}

export function buildReleaseSeoTitle(input: {
  title: string;
  artist: string;
  formatSummary: string | null;
  primaryFormat?: string | null;
  releasedAt: string | null;
  tSeo: LooseTranslator;
}): string {
  const title = inlineSeoText(input.title);
  const artist = inlineSeoText(input.artist);
  const formatSummary =
    formatSeoTitleFormat(input.formatSummary) ??
    formatSeoTitleFormat(translate(input.tSeo, "releaseFormatFallback", "physical release")) ??
    "Physical release";
  const releasedAt = cleanSeoText(input.releasedAt);
  const key = releasedAt ? "releaseTitleReleased" : "releaseTitle";
  const values = {
    title,
    artist,
    releasedAt,
  };

  const renderTitle = (format: string) => {
    const fallback = releasedAt
      ? `${title} by ${artist} on ${format}, ${releasedAt}`
      : `${title} by ${artist} on ${format}`;
    return translate(input.tSeo, key, fallback, {
      ...values,
      format,
    });
  };

  const titleText = renderTitle(formatSummary);
  if (
    titleText.length <= TITLE_LIMIT ||
    !input.primaryFormat ||
    input.primaryFormat === formatSummary
  ) {
    return trimSeoTitle(titleText);
  }

  const primaryFormat = formatSeoTitleFormat(input.primaryFormat);
  if (!primaryFormat) return trimSeoTitle(titleText);

  return trimSeoTitle(renderTitle(primaryFormat));
}

/**
 * The one indexable year filter: an exact offered decade ("anos 80"). Other
 * ranges would make an unbounded crawl space, so they stay noindex.
 */
function offersIndexableDecade(search: OffersSeoInput): [number, number] | null {
  if (!search.year?.length) return null;
  const [from, to] = yearBoundsFromParam(search.year);
  const decade = DISCOVERY_DECADES.find(
    (candidate) => candidate.startYear === from && candidate.endYear === to,
  );
  return decade ? [decade.startYear, decade.endYear] : null;
}

function hasOffersPaginationOrRangeFilter(search: OffersSeoInput) {
  if (search.cursor) return true;
  if (search.priceMin !== undefined) return true;
  if (search.year?.length && !offersIndexableDecade(search)) return true;
  return search.priceMax !== undefined;
}

function offersTaxonomyFilter(search: OffersSeoInput): { genre?: string; style?: string } | null {
  const genres = uniqueNonEmpty([...valueToArray(search.genre)]);
  const styles = uniqueNonEmpty([...valueToArray(search.style)]);
  if (genres.length + styles.length > 1) return null;

  const genre = genres[0];
  const style = styles[0];
  if (genre && !Object.hasOwn(taxonomyCatalogByLocale.en.genres, genre)) return null;
  if (style && !Object.hasOwn(taxonomyCatalogByLocale.en.styles, style)) return null;
  return { genre, style };
}

function offersArtistIds(search: OffersSeoInput) {
  return uniqueNonEmpty([...valueToArray(search.artistId)]);
}

function hasInvalidOffersArtistFilter(search: OffersSeoInput) {
  const artistIds = offersArtistIds(search);
  return artistIds.length > 1 || artistIds.some((artistId) => !isCatalogId(artistId));
}

export function isIndexableOffers(search: OffersSeoInput): boolean {
  return search.condition !== "used" && isIndexableNewOffers(search);
}

function isIndexableNewOffers(search: OffersSeoInput): boolean {
  if (hasInvalidOffersArtistFilter(search)) return false;
  if (hasOffersPaginationOrRangeFilter(search)) return false;
  if (!offersTaxonomyFilter(search)) return false;
  if (search.format && search.format !== "all" && !isOffersFormat(search.format)) return false;
  if (search.format === "other") return false;
  if (search.sort && !isOffersSort(search.sort)) return false;
  return true;
}

export function buildOffersCanonicalPath(search: OffersSeoInput): string {
  if (search.condition === "used")
    return `/offers${stringifySearch({ condition: "used", artistId: search.artistId, format: search.format, genre: search.genre, style: search.style, year: search.year, priceMin: search.priceMin, priceMax: search.priceMax, sort: search.sort, cursor: search.cursor })}`;
  if (!isIndexableOffers(search)) return "/offers";
  const taxonomy = offersTaxonomyFilter(search);
  const artistId = offersArtistIds(search)[0];

  return `/offers${stringifySearch({
    artistId,
    format: getOffersFormat(search.format) ?? undefined,
    genre: taxonomy?.genre,
    style: taxonomy?.style,
    year: offersIndexableDecade(search) ?? undefined,
  })}`;
}

export function buildOffersSeoTitle(
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
  locale: string,
): string {
  const format = getOffersFormat(search.format);
  const explicitSort = getOffersSort(search.sort);
  const taxonomy = formatOffersTaxonomy(search, translators, locale);

  if (
    search.condition !== "used" &&
    !format &&
    !explicitSort &&
    !taxonomy &&
    !hasOffersPriceFilter(search) &&
    !offersYearPhrase(search) &&
    !search.artistName
  ) {
    return trimSeoTitle(
      translate(translators.offers, "offersSeoTitleDefault", "Music Offers and Price Drops"),
    );
  }

  return trimSeoTitle(buildOffersSeoHeading(search, translators, locale));
}

export function buildOffersSeoHeading(
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
  locale: string,
): string {
  const format = getOffersFormat(search.format);
  const taxonomy = formatOffersTaxonomy(search, translators, locale);
  const sort = getOffersSort(search.sort) ?? "price_drop";

  const heading =
    !format && !taxonomy && !search.sort
      ? translate(translators.offers, "offersPageTitle", "Offers on vinyl, CDs and other formats", {
          condition: search.condition ?? "new",
        })
      : buildFilteredOffersHeading(format, taxonomy, sort, translators, locale, search.condition);

  const artistHeading = search.artistName
    ? translate(
        translators.offers,
        "offersSeoHeadingArtist",
        `${heading} by ${search.artistName}`,
        { artist: search.artistName, heading },
      )
    : heading;
  const yearHeading = formatOffersYearHeading(artistHeading, search, translators);

  return formatOffersPriceHeading(yearHeading, search, translators, locale);
}

type OffersYearPhrase =
  | { kind: "decade"; start: string; decade: string }
  | { kind: "year" | "from"; start: string }
  | { kind: "to"; end: string }
  | { kind: "other"; start: string; end: string };

/** Reads the original-year filter bounds; the supported limits mean an open bound. */
function offersYearBounds(search: OffersSeoInput) {
  const [from, to] = yearBoundsFromParam(search.year);
  return {
    lower: from !== undefined && from > OFFER_YEAR_MIN ? from : undefined,
    upper: to !== undefined && to < OFFER_YEAR_MAX ? to : undefined,
  };
}

function offersYearPhrase(search: OffersSeoInput): OffersYearPhrase | null {
  const { lower, upper } = offersYearBounds(search);
  if (lower === undefined) {
    return upper === undefined ? null : { kind: "to", end: String(upper) };
  }
  if (upper === undefined) return { kind: "from", start: String(lower) };
  if (lower === upper) return { kind: "year", start: String(lower) };
  if (lower % 10 === 0 && upper === lower + 9) {
    // "anos 80" / "años 80", but "anos 2000": two digits only before 2000.
    const decade = lower < 2000 ? String(lower % 100).padStart(2, "0") : String(lower);
    return { kind: "decade", start: String(lower), decade };
  }
  return { kind: "other", start: String(lower), end: String(upper) };
}

function offersYearHeadingFallback(heading: string, phrase: OffersYearPhrase): string {
  switch (phrase.kind) {
    case "decade":
      return `${heading} from the ${phrase.start}s`;
    case "year":
      return `${heading} from ${phrase.start}`;
    case "from":
      return `${heading} since ${phrase.start}`;
    case "to":
      return `${heading} up to ${phrase.end}`;
    case "other":
      return `${heading} from ${phrase.start} to ${phrase.end}`;
  }
}

function formatOffersYearHeading(
  heading: string,
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
): string {
  const phrase = offersYearPhrase(search);
  if (!phrase) return heading;
  return translate(
    translators.offers,
    "offersSeoHeadingYears",
    offersYearHeadingFallback(heading, phrase),
    { start: "", end: "", decade: "", ...phrase, heading },
  );
}

function buildFilteredOffersHeading(
  format: OffersFormat | null,
  taxonomy: string | null,
  sort: OffersSort,
  translators: OffersSeoTranslators,
  locale: string,
  condition: OffersSeoInput["condition"],
): string {
  const values = {
    condition: condition ?? "new",
    format: format
      ? condition === "used"
        ? translate(translators.offers, `offersSeoUsedFormat.${format}`, format)
        : formatOffersFormat(format, translators, locale)
      : null,
    subject: format ? formatOffersBestPriceSubject(format, translators, condition) : null,
    taxonomy,
  };
  const variant = offersHeadingVariant(Boolean(format), Boolean(taxonomy));
  const key = `offersSeoHeading${offersHeadingSortKey(sort)}${variant}`;
  const fallback = offersHeadingFallback(sort, values);

  return translate(translators.offers, key, fallback, values);
}

function hasOffersPriceFilter(search: OffersSeoInput): boolean {
  return search.priceMin !== undefined || search.priceMax !== undefined;
}

function formatOffersPriceHeading(
  heading: string,
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
  locale: string,
): string {
  const currency = search.currency ?? DEFAULT_VIEWER_CURRENCY;
  const minimum =
    search.priceMin === undefined ? null : formatPrice(search.priceMin, currency, locale);
  const maximum =
    search.priceMax === undefined ? null : formatPrice(search.priceMax, currency, locale);

  if (minimum) {
    if (maximum) {
      if (search.priceMin === search.priceMax) {
        return translate(
          translators.offers,
          "offersSeoHeadingAtPrice",
          `${heading} at ${minimum}`,
          { heading, price: minimum },
        );
      }
      return translate(
        translators.offers,
        "offersSeoHeadingPriceRange",
        `${heading} from ${minimum} to ${maximum}`,
        { heading, minimum, maximum },
      );
    }
    return translate(
      translators.offers,
      "offersSeoHeadingPriceFrom",
      `${heading} from ${minimum}`,
      {
        heading,
        minimum,
      },
    );
  }
  if (maximum) {
    return translate(translators.offers, "offersSeoHeadingPriceTo", `${heading} up to ${maximum}`, {
      heading,
      maximum,
    });
  }
  return heading;
}

function formatOffersTaxonomy(
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
  locale: string,
): string | null {
  return formatSeoTaxonomyList({
    genres: [...valueToArray(search.genre)],
    styles: [...valueToArray(search.style)],
    translators,
    locale,
  });
}

function offersHeadingVariant(hasFormat: boolean, hasTaxonomy: boolean): string {
  if (hasFormat && hasTaxonomy) return "FormatTaxonomy";
  if (hasFormat) return "Format";
  if (hasTaxonomy) return "Taxonomy";
  return "";
}

function formatOffersBestPriceSubject(
  format: OffersFormat,
  translators: OffersSeoTranslators,
  condition: OffersSeoInput["condition"],
): string {
  const fallback = `Lowest-priced ${fallbackOffersFormatLabel(format).toLocaleLowerCase("en")}`;
  return translate(translators.offers, `offersSeoBestPriceSubject.${format}`, fallback, {
    condition: condition ?? "new",
  });
}

function offersHeadingSortKey(sort: OffersSort): string {
  switch (sort) {
    case "best_price":
      return "BestPrice";
    case "recent":
      return "Recent";
    case "price_drop":
      return "BestDeals";
  }
}

function offersHeadingFallback(
  sort: OffersSort,
  values: { format: string | null; subject: string | null; taxonomy: string | null },
): string {
  const taxonomySuffix = values.taxonomy ? ` in ${values.taxonomy}` : "";
  if (sort === "best_price") {
    return `${values.subject ?? "Lowest-priced offers"}${taxonomySuffix}`;
  }
  const format = values.format ? ` ${values.format.toLocaleLowerCase("en")}` : "";
  const prefix = sort === "recent" ? "Latest" : "Best";
  return `${prefix}${format} offers${taxonomySuffix}`;
}

export function buildOffersSeoDescription(
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
  locale: string,
): string {
  const description = buildOffersSeoDescriptionWithoutArtist(search, translators, locale);
  if (!search.artistName) return description;

  return buildSeoDescription([
    translate(
      translators.offers,
      "offersSeoDescriptionArtist",
      `${description} Browse current physical releases by ${search.artistName}.`,
      { artist: search.artistName, description },
    ),
  ]);
}

function buildOffersSeoDescriptionWithoutArtist(
  search: OffersSeoInput,
  translators: OffersSeoTranslators,
  locale: string,
): string {
  const format = getOffersFormat(search.format);
  const explicitSort = getOffersSort(search.sort);

  if (!format && !explicitSort) {
    return buildSeoDescription([
      translate(
        translators.offers,
        "offersSeoDescriptionDefault",
        "Compare current music release offers, price drops, and best prices across vinyl, CDs, cassettes, and other physical formats.",
      ),
    ]);
  }

  const sort = explicitSort ?? "price_drop";
  const sortLabel = formatOffersSortLabel(sort, translators);
  const normalizedSort = sortLabel.toLocaleLowerCase(locale);

  if (format) {
    return buildSeoDescription([
      translate(
        translators.offers,
        "offersSeoDescriptionWithFormat",
        `Compare ${normalizedSort} music release offers on ${format}, matched through compatible catalog releases.`,
        {
          sort: normalizedSort,
          format: formatOffersFormat(format, translators, locale),
        },
      ),
    ]);
  }

  return buildSeoDescription([
    translate(
      translators.offers,
      "offersSeoDescriptionSorted",
      `Compare ${normalizedSort} music release offers matched through compatible catalog releases.`,
      { sort: normalizedSort },
    ),
  ]);
}

function formatSeoTitleFormat(value: string | null | undefined): string | null {
  const cleaned = cleanSeoText(value);
  return cleaned ? sentenceCase(cleaned, "und") : null;
}

function getOffersFormat(value: string | null | undefined): OffersFormat | null {
  if (!value || value === "all") return null;
  return isOffersFormat(value) ? value : null;
}

function getOffersSort(value: string | null | undefined): OffersSort | null {
  return isOffersSort(value) ? value : null;
}

function formatOffersSortLabel(sort: OffersSort, translators: OffersSeoTranslators): string {
  const meta = offersSortMeta(sort);
  return translate(translators.offers, meta.key, meta.fallback);
}

function offersSortMeta(sort: OffersSort): { key: string; fallback: string } {
  switch (sort) {
    case "best_price":
      return { key: "offersSeoSortBestPrices", fallback: "Best Prices" };
    case "recent":
      return { key: "offersSeoSortLatest", fallback: "Latest" };
    case "price_drop":
      return { key: "offersSeoSortBestDeals", fallback: "Best Deals" };
  }
}

function formatOffersFormat(
  format: OffersFormat,
  translators: OffersSeoTranslators,
  locale: string,
): string {
  if (format === "cd") return translate(translators.offers, "offersCdLabel", "CDs");
  return sentenceCase(
    translate(translators.collection, format, fallbackOffersFormatLabel(format)),
    locale,
  );
}

function fallbackOffersFormatLabel(format: OffersFormat): string {
  switch (format) {
    case "vinyl":
      return "Vinyl";
    case "cd":
      return "CD";
    case "cassette":
      return "Cassette";
    case "video":
      return "Video";
    case "other":
      return "Other formats";
  }
}

function isOffersFormat(value: string | null | undefined): value is OffersFormat {
  return Boolean(value && (FORMAT_CATEGORIES_WITH_OTHER as readonly string[]).includes(value));
}

function isOffersSort(value: string | null | undefined): value is OffersSort {
  return value === "best_price" || value === "price_drop" || value === "recent";
}

export function isSearchBrowseState(search: SearchSeoInput): boolean {
  if (hasSearchNavigationState(search)) return false;
  return !hasUnsupportedBrowseFilter(search);
}

function hasSearchNavigationState(search: SearchSeoInput): boolean {
  if (search.q?.trim()) return true;
  if (search.tab && search.tab !== "music") return true;
  if (search.page && search.page > 1) return true;
  return Boolean(search.forSale || search.sort || search.cursor);
}

function hasUnsupportedBrowseFilter(search: SearchSeoInput): boolean {
  if (search.label?.length) return true;
  if (search.company?.length) return true;
  if (search.type && !isSearchType(search.type)) return true;
  if (search.type === "other") return true;
  return Boolean(search.format && !isSearchFormat(search.format));
}

export function buildSearchCanonicalPath(search: SearchSeoInput): string {
  // SSR `<link rel="canonical">` needs a string — the one place that serializes
  // through the codec directly rather than a React `<Link search>`.
  return `/search${stringifySearch({
    q: search.q?.trim() || undefined,
    tab: search.tab && search.tab !== "music" ? search.tab : undefined,
    type: search.type,
    format: search.format,
    genre: search.genre,
    style: search.style,
    year: search.year,
    country: search.country,
    origin: search.origin,
    label: search.label,
    company: search.company,
    page: search.page && search.page > 1 ? search.page : undefined,
    sale: search.forSale || undefined,
    sort: search.sort,
    cursor: search.cursor,
  })}`;
}

export function buildSearchFilterPhrase(
  search: SearchSeoInput,
  translators: SearchSeoTranslators,
  locale: string,
): string | null {
  if (!isSearchBrowseState(search)) return null;

  const typeLabel = isSearchType(search.type) ? formatSearchType(search.type, translators) : null;
  const formatLabel = isSearchFormat(search.format)
    ? formatSearchFormat(search.format, translators)
    : null;
  const genreStyle = formatGenreStylePhrase(search, translators);
  const subject = formatSearchSubject({
    typeLabel,
    formatLabel,
    genreStyle,
    translators,
  });
  const qualifiers = searchFilterQualifiers(search, translators, locale);

  const hasSubjectLabel = Boolean(typeLabel || formatLabel || genreStyle);
  const hasQualifiers = qualifiers.length > 0;

  if (!hasSubjectLabel && !hasQualifiers) return null;
  if (!hasQualifiers) return subject;

  const filterList = listFormat(locale, qualifiers);
  return translate(translators.seo, "searchFilterPhrase", `${subject} ${filterList}`, {
    subject,
    filters: filterList,
    filterList,
  });
}

function searchFilterQualifiers(
  search: SearchSeoInput,
  translators: SearchSeoTranslators,
  locale: string,
): string[] {
  const country = search.country
    ? translate(translators.seo, "searchReleasedIn", `released in ${search.country}`, {
        country: formatCountry(search.country, locale) || search.country,
      })
    : null;
  const origin = search.origin
    ? translate(translators.seo, "searchArtistCountry", `by artists from ${search.origin}`, {
        country: formatCountry(search.origin, locale) || search.origin,
      })
    : null;
  return [formatYearPhrase(search, translators), country, origin].filter((value): value is string =>
    Boolean(value),
  );
}

function formatSearchSubject(input: {
  typeLabel: string | null;
  formatLabel: string | null;
  genreStyle: string | null;
  translators: SearchSeoTranslators;
}) {
  const { typeLabel, formatLabel, genreStyle, translators } = input;

  if (genreStyle) {
    return formatGenreStyleSubject(typeLabel, formatLabel, genreStyle, translators);
  }
  return formatPlainSearchSubject(typeLabel, formatLabel, translators);
}

function formatGenreStyleSubject(
  typeLabel: string | null,
  formatLabel: string | null,
  genreStyle: string,
  translators: SearchSeoTranslators,
): string {
  if (typeLabel && formatLabel) {
    return translate(
      translators.seo,
      "searchTypeGenreStyleOnFormat",
      `${genreStyle} ${typeLabel} on ${formatLabel}`,
      { type: typeLabel, format: formatLabel, genreStyle },
    );
  }

  if (typeLabel) {
    return translate(translators.seo, "searchTypeGenreStyle", `${genreStyle} ${typeLabel}`, {
      type: typeLabel,
      genreStyle,
    });
  }

  if (formatLabel) {
    return translate(
      translators.seo,
      "searchGenreStyleOnFormat",
      `${genreStyle} on ${formatLabel}`,
      { format: formatLabel, genreStyle },
    );
  }

  return genreStyle;
}

function formatPlainSearchSubject(
  typeLabel: string | null,
  formatLabel: string | null,
  translators: SearchSeoTranslators,
): string {
  if (typeLabel && formatLabel) {
    return translate(translators.seo, "searchTypeOnFormat", `${typeLabel} on ${formatLabel}`, {
      type: typeLabel,
      format: formatLabel,
    });
  }

  if (typeLabel) return typeLabel;

  if (formatLabel) {
    return translate(translators.seo, "searchFormatSubject", `${formatLabel} releases`, {
      format: formatLabel,
    });
  }

  return translate(translators.seo, "searchCatalogSubject", "music releases");
}

export function buildSearchSeoTitle(
  search: SearchSeoInput,
  translators: SearchSeoTranslators,
  locale: string,
): string {
  return trimSeoTitle(buildSearchSeoHeading(search, translators, locale));
}

export function buildSearchSeoHeading(
  search: SearchSeoInput,
  translators: SearchSeoTranslators,
  locale: string,
): string {
  const phrase = buildSearchFilterPhrase(search, translators, locale);
  if (isSearchBrowseState(search) && phrase) {
    return translate(translators.seo, "searchBrowseTitle", `Browse ${phrase}`, {
      phrase: sentenceCase(phrase, locale),
    });
  }

  const query = search.q?.trim();
  if (query) {
    return translate(translators.seo, "searchQueryTitle", `${query} music catalog search`, {
      query,
    });
  }

  return translate(
    translators.seo,
    "searchTitle",
    "Search vinyl records, CDs, cassettes, artists and labels",
  );
}

function formatGenreStylePhrase(
  search: SearchSeoInput,
  translators: SearchSeoTranslators,
): string | null {
  const genre = search.genre ? formatTranslatedTaxonomy(search.genre, translators.genres) : null;
  const style = search.style ? formatTranslatedTaxonomy(search.style, translators.styles) : null;
  if (genre && style) {
    return translate(translators.seo, "searchGenreStyle", `${genre} and ${style}`, {
      genre,
      style,
    });
  }
  return genre ?? style;
}

function formatYearPhrase(
  search: SearchSeoInput,
  translators: SearchSeoTranslators,
): string | null {
  const [from, to] = yearBoundsFromParam(search.year);
  if (from && to) {
    const decade = decadeLabel(from, to);
    if (decade) {
      return translate(translators.seo, "searchDecade", `from the ${decade}`, { decade });
    }
    return translate(translators.seo, "searchYearRange", `from ${from} to ${to}`, { from, to });
  }
  if (from) return translate(translators.seo, "searchYearFrom", `from ${from}`, { from });
  if (to) return translate(translators.seo, "searchYearTo", `up to ${to}`, { to });
  return null;
}

function decadeLabel(from: number, to: number): string | null {
  if (to - from !== 9) return null;
  if (from % 10 !== 0) return null;
  return `${from}s`;
}

function formatSearchType(type: SearchType, translators: SearchSeoTranslators): string | null {
  if (type === "other") return null;
  return translate(
    translators.seo,
    `typeLabels.${type}`,
    translate(translators.albumType, type, fallbackTypeLabel(type)),
  );
}

function formatSearchFormat(format: SearchFormat, translators: SearchSeoTranslators): string {
  return translate(
    translators.seo,
    `formatLabels.${format}`,
    translate(translators.formats, format, fallbackFormatLabel(format)),
  );
}

function formatTranslatedTaxonomy(value: string, translator: LooseTranslator): string {
  return translate(translator, value, formatCatalogTaxonomyLabel(value));
}

function isSearchType(value: string | null | undefined): value is SearchType {
  return Boolean(value && (ALBUM_TYPES as readonly string[]).includes(value));
}

function isSearchFormat(value: string | null | undefined): value is SearchFormat {
  return Boolean(value && (FORMAT_CATEGORIES as readonly string[]).includes(value));
}

function fallbackTypeLabel(type: AlbumType): string {
  switch (type) {
    case "album":
      return "albums";
    case "compilation":
      return "compilations";
    case "single_ep":
      return "singles and EPs";
    case "live":
      return "live albums";
    case "video":
      return "videos";
    case "other":
      return "other";
  }
}

function fallbackFormatLabel(format: SearchFormat): string {
  switch (format) {
    case "vinyl":
      return "vinyl";
    case "cd":
      return "CDs";
    case "cassette":
      return "cassettes";
    case "video":
      return "videos";
  }
}
